<template>
  <v-dialog
    :model-value="modelValue"
    :max-width="smAndDown ? undefined : 760"
    :fullscreen="smAndDown"
    :transition="smAndDown ? 'dialog-bottom-transition' : 'dialog-transition'"
    :content-class="
      smAndDown
        ? 'magic-link-sent-dialog magic-link-sent-dialog--mobile'
        : 'magic-link-sent-dialog'
    "
    @update:model-value="onDialogToggle"
  >
    <div class="magic-link-sent">
      <button class="close-btn" type="button" @click="closeDialog">
        <v-icon icon="mdi-close" />
      </button>

      <img class="brand-logo" src="/img/logo-f-123x190.webp" alt="FlixLatino" />

      <h2 class="title">{{ $t("avodDialogs.magicLinkSent.title") }}</h2>
      <p class="subtitle">
        {{ $t("avodDialogs.magicLinkSent.subtitle", { email }) }}
      </p>

      <button
        class="cta-btn"
        type="button"
        :disabled="submitting || !email"
        @click="resendMagicLink"
      >
        {{ submitting ? $t("avodDialogs.magicLinkSent.sending") : $t("avodDialogs.magicLinkSent.resend") }}
      </button>

      <div v-if="showSentStatus" class="status-pill">
        <v-icon icon="mdi-check" size="14" />
        <span>{{ $t("avodDialogs.magicLinkSent.sent") }}</span>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useDisplay } from "vuetify";
import { FlixlatinoApiClient } from "../../../services/FlixlatinoApiClient";

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    email?: string;
  }>(),
  {
    modelValue: false,
    email: "",
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "close"): void;
  (e: "resent", payload: { email: string; response: any }): void;
}>();

const { smAndDown } = useDisplay();
const apiClient = new FlixlatinoApiClient();
const submitting = ref(false);
const showSentStatus = ref(false);

watch(
  () => props.modelValue,
  (value) => {
    if (value) {
      showSentStatus.value = true;
    }
  },
  { immediate: true }
);

function onDialogToggle(value: boolean) {
  emit("update:modelValue", value);
  if (!value) emit("close");
}

function closeDialog() {
  emit("update:modelValue", false);
  emit("close");
}

async function resendMagicLink() {
  const email = String(props.email || "").trim();
  if (!email || submitting.value) {
    return;
  }

  submitting.value = true;
  try {
    const response = await apiClient.magicLink(email);
    const responseData = response as any;
    console.debug("magic-link response", responseData);
    if (Number(responseData?.code) !== 200) {
      return;
    }

    showSentStatus.value = true;
    emit("resent", { email, response: responseData });
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.magic-link-sent {
  position: relative;
  width: 760px;
  min-height: 600px;
  border-radius: 48px;
  border: 1px solid transparent;
  background: radial-gradient(
      75% 80% at 30% 20%,
      rgba(57, 66, 118, 0.6) 0%,
      rgba(31, 39, 81, 0.38) 38%,
      rgba(16, 24, 57, 0.9) 100%
    ),
    linear-gradient(180deg, #1c2650 0%, #11183b 100%);
  padding: 64px 56px 52px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  box-sizing: border-box;
}

.magic-link-sent::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 3rem;
  padding: 2px;
  background: linear-gradient(
    135deg,
    rgba(158, 120, 255, 0.95) 0%,
    rgba(119, 86, 255, 0.9) 35%,
    rgba(255, 64, 153, 0.9) 70%,
    rgba(255, 64, 153, 0.95) 100%
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  color: #f2f5ff;
}

.brand-logo {
  width: 82px;
  height: auto;
  margin-bottom: 28px;
}

.title {
  margin: 0 0 10px 0;
  font-size: 2rem;
  line-height: 120%;
  font-weight: 800;
  letter-spacing: 0;
}

.subtitle {
  margin: 0;
  max-width: 430px;
  color: #bac7f3;
  font-size: 1.18rem;
  line-height: 1.5;
}

.subtitle strong {
  color: #fff;
  font-weight: 800;
}

.cta-btn {
  margin-top: 30px;
  height: 56px;
  border: none;
  border-radius: 14px;
  background: #fa002b;
  color: #fff;
  font-size: 1rem;
  font-weight: 900;
  min-width: 230px;
  padding: 0 24px;
}

.cta-btn:disabled {
  opacity: 0.72;
}

.status-pill {
  margin-top: 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: linear-gradient(90deg, #9e63d1 0%, #b04a8f 100%);
  color: #fff;
  font-size: 0.875rem;
  line-height: 1;
}

@media (max-width: 959px) {
  :deep(.magic-link-sent-dialog--mobile) {
    margin: 0;
    width: 100vw !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    height: 100vh !important;
  }

  .magic-link-sent {
    border-radius: 0;
    min-height: 100vh;
    width: 100%;
    padding: 42px 20px 30px;
  }

  .magic-link-sent::before {
    border-radius: 0;
  }

  .close-btn {
    top: 12px;
    right: 12px;
  }

  .brand-logo {
    width: 48px;
    margin-bottom: 18px;
  }

  .title {
    font-size: 30px;
    margin-bottom: 8px;
  }

  .subtitle {
    font-size: 18px;
    line-height: 1.45;
    max-width: 320px;
  }

  .cta-btn {
    margin-top: 24px;
    min-width: 0;
    width: 100%;
    max-width: 320px;
  }

  .status-pill {
    margin-top: 16px;
  }
}
</style>
